import type { UserPreferences } from '@skychat/shared-core';
import enMessages from '../../locales/en.json';
import itMessages from '../../locales/it.json';
import zhMessages from '../../locales/zh.json';

type ComposerActionsSide = 'left' | 'right';

export type MobilePreferences = Required<Pick<UserPreferences, 'themeMode' | 'locale' | 'webpushEnabled' | 'previewsEnabled' | 'mediaPreviewEnabled' | 'messageAvatarMode' | 'petBubblesEnabled' | 'showStatusOnAvatar'>> & {
  accentColor: string;
  composerActionsSide: ComposerActionsSide;
  weather?: Record<string, unknown>;
};

export const DEFAULT_PREFERENCES: MobilePreferences = {
  themeMode: 'auto',
  locale: 'en',
  webpushEnabled: true,
  previewsEnabled: true,
  mediaPreviewEnabled: true,
  messageAvatarMode: 'peer',
  petBubblesEnabled: true,
  showStatusOnAvatar: false,
  accentColor: '#2377ff',
  composerActionsSide: 'left'
};

export const ACCENT_PRESETS = ['#2377ff', '#0ea5e9', '#14b8a6', '#22c55e', '#a855f7', '#ec4899', '#f97316', '#ef4444'] as const;

export const LOCALE_RESOURCES: Record<'en' | 'it' | 'zh', Record<string, string>> = {
  en: enMessages,
  it: itMessages,
  zh: zhMessages
};

export function mobileTranslation(locale: MobilePreferences['locale'], key: string): string | undefined {
  const normalizedLocale = locale === 'zh' || locale === 'it' ? locale : 'en';
  return LOCALE_RESOURCES[normalizedLocale][key] || LOCALE_RESOURCES.en[key];
}

export function normalizeAccentColor(value: unknown): string {
  const raw = String(value || '').trim();
  const hex = raw.startsWith('#') ? raw : `#${raw}`;
  return /^#[0-9a-f]{6}$/i.test(hex) ? hex.toLowerCase() : DEFAULT_PREFERENCES.accentColor;
}

export function accentRgb(value: string): string {
  const hex = normalizeAccentColor(value).slice(1);
  return [0, 2, 4].map((offset) => Number.parseInt(hex.slice(offset, offset + 2), 16)).join(', ');
}

export function accentSecondary(value: string): string {
  const normalized = normalizeAccentColor(value);
  if (normalized === '#14b8a6' || normalized === '#22c55e') return '#2377ff';
  if (normalized === '#f97316' || normalized === '#ef4444') return '#14b8a6';
  if (normalized === '#ec4899' || normalized === '#a855f7') return '#06b6d4';
  return '#24c7a1';
}

export function browserLocale(language = typeof navigator === 'undefined' ? '' : navigator.language): MobilePreferences['locale'] {
  const normalized = String(language || '').toLowerCase();
  if (normalized.startsWith('zh')) return 'zh';
  if (normalized.startsWith('it')) return 'it';
  return 'en';
}

export function normalizePreferences(input?: (Partial<UserPreferences> & Record<string, unknown>) | null): MobilePreferences {
  const locale = input?.locale || input?.lang || DEFAULT_PREFERENCES.locale;
  const messageAvatarMode = input?.messageAvatarMode || DEFAULT_PREFERENCES.messageAvatarMode;
  const composerActionsSide = String(input?.composerActionsSide || DEFAULT_PREFERENCES.composerActionsSide);
  return {
    ...DEFAULT_PREFERENCES,
    ...input,
    locale: locale === 'zh' || locale === 'it' || locale === 'en' ? locale : DEFAULT_PREFERENCES.locale,
    themeMode: input?.themeMode === 'light' || input?.themeMode === 'dark' || input?.themeMode === 'auto' ? input.themeMode : DEFAULT_PREFERENCES.themeMode,
    webpushEnabled: input?.webpushEnabled !== false,
    previewsEnabled: input?.previewsEnabled !== false,
    mediaPreviewEnabled: input?.mediaPreviewEnabled !== false,
    messageAvatarMode: messageAvatarMode === 'both' || messageAvatarMode === 'none' || messageAvatarMode === 'peer' ? messageAvatarMode : DEFAULT_PREFERENCES.messageAvatarMode,
    petBubblesEnabled: input?.petBubblesEnabled !== false,
    showStatusOnAvatar: input?.showStatusOnAvatar === true,
    accentColor: normalizeAccentColor(input?.accentColor),
    composerActionsSide: composerActionsSide === 'right' ? 'right' : 'left'
  };
}
